import React, { useState, useEffect, useRef } from 'react';
import { useNavigateToDetail } from '../../../hooks/useNavigateToDetail';
import DetailLink from '../../Common/DetailLink';
import { 
  MapPinIcon, 
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  Squares2X2Icon,
  ListBulletIcon,
  PhoneIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import adminAPI from '../../../services/adminApi';
import { toast } from 'react-hot-toast';
import {
  CardGridSkeleton,
  DataViewLoadingContainer,
  TableSkeleton,
} from '../../RBAC/Common/DataViewLoading';
import LocationForm from './LocationForm';
import PermissionGuard from '../../Common/PermissionGuard';
import TextInput from '../../Common/TextInput';
import CustomSelect from '../../Common/CustomSelect';

const LocationsList = () => {
  const navigateToDetail = useNavigateToDetail();
  // Get initial view from localStorage or default to 'card'
  const getInitialView = () => {
    try {
      const saved = localStorage.getItem('locationsView');
      return saved || 'card';
    } catch {
      return 'card';
    }
  };

  const [view, setView] = useState(getInitialView());
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [localTypeFilter, setLocalTypeFilter] = useState(null);
  const [localActiveFilter, setLocalActiveFilter] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    limit: 20,
    total: 0,
    pages: 1
  });
  const searchTimeoutRef = useRef(null);

  // Default filters
  const defaultFilters = {
    type: null,
    isActive: true,
    search: ''
  };

  // Save view preference to localStorage
  const handleViewChange = (newView) => {
    setView(newView);
    try {
      localStorage.setItem('locationsView', newView);
    } catch (error) {
      console.error('Failed to save view preference:', error);
    }
  };

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.limit, localTypeFilter, localActiveFilter, debouncedSearch]);

  // Debounced search handler — only fetch after typing settles
  const handleSearch = (searchTerm) => {
    setSearchInput(searchTerm);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPagination(prev => ({ ...prev, current: 1 }));
    }, 500);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.limit,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(localTypeFilter && { type: localTypeFilter })
      };

      // Handle active/inactive filter
      if (localActiveFilter === true) {
        params.status = 'active';
      } else if (localActiveFilter === false) {
        params.status = 'inactive';
      } else {
        params.includeInactive = true; // Show all statuses
      }
      
      const response = await adminAPI.getLocations(params);
      const locationsData = response.data.data || [];
      setLocations(locationsData);
      
      // Update pagination from response
      if (response.data.pagination) {
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          pages: response.data.pagination.pages
        }));
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleTypeFilter = (type) => {
    setLocalTypeFilter(type);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleActiveFilter = (isActive) => {
    setLocalActiveFilter(isActive);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setLocalTypeFilter(null);
    setLocalActiveFilter(true);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const hasModifiedFilters = () => {
    return searchInput !== '' ||
           localTypeFilter !== null ||
           localActiveFilter !== true;
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, current: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ current: 1, limit: newLimit, total: prev.total, pages: prev.pages }));
  };

  const handleDelete = async (locationId, locationName) => {
    setShowDeleteConfirm(null);
    
    try {
      await adminAPI.deleteLocation(locationId);
      toast.success('Location deleted successfully');
      fetchLocations();
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error(error.response?.data?.message || 'Failed to delete location');
    }
  };

  const getLocationIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'warehouse':
      case 'distribution_center':
      case 'cold_storage':
      case 'hazmat_facility':
        return '🏭';
      case 'branch':
      case 'regional_office':
        return '🏢';
      case 'office':
      case 'sales_office':
        return '🏛️';
      case 'factory':
        return '🏭';
      case 'headquarters':
        return '🏤';
      case 'retail_store':
        return '🏪';
      case 'service_center':
        return '🔧';
      default:
        return '📍';
    }
  };

  const getTypeColor = (type) => {
    const typeLower = type?.toLowerCase();
    switch (typeLower) {
      case 'warehouse':
      case 'distribution_center':
      case 'cold_storage':
      case 'hazmat_facility':
        return 'bg-blue-100 text-blue-800';
      case 'branch':
      case 'regional_office':
        return 'bg-green-100 text-green-800';
      case 'office':
      case 'sales_office':
        return 'bg-purple-100 text-purple-800';
      case 'factory':
        return 'bg-orange-100 text-orange-800';
      case 'headquarters':
        return 'bg-indigo-100 text-indigo-800';
      case 'retail_store':
        return 'bg-pink-100 text-pink-800';
      case 'service_center':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderLocationCard = (location) => {
    const isActive = location.status === 'active' && location.settings?.isActive !== false;
    const parentLocation = location.parentLocation;
    const contactInfo = location.contact || {};
    const isWarehouse = ['warehouse', 'distribution_center', 'cold_storage', 'hazmat_facility'].includes(location.type);
    const capacityPercentage = location.capacity?.totalSpace 
      ? (location.capacity.usedSpace / location.capacity.totalSpace) * 100 
      : 0;
    
    return (
    <div key={location._id} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 group">
      <div className="p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg font-semibold flex-shrink-0 ${getTypeColor(location.type)}`}>
              {getLocationIcon(location.type)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <h3 className="text-base font-semibold text-gray-900 truncate">
                  <DetailLink to={`/admin/locations/${location._id}`} className="hover:text-indigo-600">
                    {location.name}
                  </DetailLink>
                </h3>
                {location.settings?.isDefault && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Default
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 font-mono mb-1">{location.code}</p>
              {parentLocation && (
                <p className="text-xs text-indigo-600 truncate mb-1" title={`Parent: ${typeof parentLocation === 'object' ? parentLocation.name : 'Parent Location'}`}>
                  ↳ {typeof parentLocation === 'object' ? parentLocation.name : 'Sublocation'}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
            <button
              onClick={() => navigateToDetail(`/admin/locations/${location._id}`)}
              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all"
              title="View location"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
            <PermissionGuard permission="ADMIN:LOCATIONS:UPDATE">
              <button
                onClick={() => {
                  setEditingLocation(location);
                  setShowLocationForm(true);
                }}
                className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-all"
                title="Edit location"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
            </PermissionGuard>

            <PermissionGuard permission="ADMIN:LOCATIONS:DELETE">
              <button
                onClick={() => setShowDeleteConfirm(location)}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all"
                title="Delete location"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </PermissionGuard>
          </div>
        </div>

        {/* Description */}
        {location.description && (
          <div className="mb-3">
            <p className="text-sm text-gray-600 line-clamp-2" title={location.description}>
              {location.description}
            </p>
          </div>
        )}

        {/* Address */}
        {location.address?.city && (
          <div className="mb-3">
            <div className="flex items-start space-x-2">
              <MapPinIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-600 line-clamp-2 leading-tight">
                {location.address.street && `${location.address.street}, `}
                {location.address.city}
                {location.address.state && `, ${location.address.state}`}
                {location.address.zipCode && ` ${location.address.zipCode}`}
              </p>
            </div>
          </div>
        )}

        {/* Contact Info */}
        {(contactInfo.phone || contactInfo.email) && (
          <div className="mb-3 space-y-1">
            {contactInfo.phone && (
              <div className="flex items-center space-x-2">
                <PhoneIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="text-xs text-gray-600 truncate">{contactInfo.phone}</span>
              </div>
            )}
            {contactInfo.email && (
              <div className="flex items-center space-x-2">
                <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
                <span className="text-xs text-gray-600 truncate">{contactInfo.email}</span>
              </div>
            )}
            {contactInfo.manager?.name && (
              <div className="flex items-center space-x-2">
                <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-xs text-gray-600 truncate">Manager: {contactInfo.manager.name}</span>
              </div>
            )}
          </div>
        )}

        {/* Warehouse Capacity (for warehouse types) */}
        {isWarehouse && location.capacity?.totalSpace > 0 && (
          <div className="mb-3 p-2 bg-blue-50 rounded-md">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-blue-900">Capacity</span>
              <span className="text-xs text-blue-700">{Math.round(capacityPercentage)}%</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-1.5 mb-1">
              <div 
                className="bg-blue-600 h-1.5 rounded-full transition-all duration-300" 
                style={{ width: `${Math.min(capacityPercentage, 100)}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-blue-700">
              <span>{location.capacity.usedSpace?.toLocaleString() || 0} {location.capacity.unit}</span>
              <span>{location.capacity.totalSpace?.toLocaleString() || 0} {location.capacity.unit}</span>
            </div>
          </div>
        )}

        {/* Statistics */}
        {location.statistics && (location.statistics.totalUsers > 0 || location.statistics.totalEmployees > 0) && (
          <div className="mb-3 flex items-center space-x-4 text-xs text-gray-500">
            {location.statistics.totalUsers > 0 && (
              <div className="flex items-center space-x-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                <span>{location.statistics.totalUsers} users</span>
              </div>
            )}
            {location.statistics.totalEmployees > 0 && (
              <div className="flex items-center space-x-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                </svg>
                <span>{location.statistics.totalEmployees} employees</span>
              </div>
            )}
          </div>
        )}

        {/* Tags and Status Footer */}
        <div className="flex flex-wrap gap-1.5 mt-auto">
          <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getTypeColor(location.type)}`}>
            {location.type?.replace('_', ' ').toUpperCase()}
          </span>
          
          {isActive ? (
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
              ✓ Active
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-100 text-red-800">
              ✕ Inactive
            </span>
          )}

          {/* Special Settings Badges */}
          {location.settings?.temperatureControlled && (
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-cyan-100 text-cyan-800" title="Temperature Controlled">
              ❄️ Climate
            </span>
          )}
          
          {location.settings?.hazardousMaterials && (
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-800" title="Hazardous Materials">
              ⚠️ Hazmat
            </span>
          )}

          {location.settings?.autoReorderEnabled && (
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800" title="Auto Reorder Enabled">
              🔄 Auto
            </span>
          )}
        </div>

        {/* Last Updated */}
        {location.updatedAt && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-400">
              Updated {new Date(location.updatedAt).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>
    </div>
  )};


  const renderLocationRow = (location) => {
    const isActive = location.status === 'active' && location.settings?.isActive !== false;
    const contactInfo = location.contact || location.contactInfo || {};
    const parentLocation = location.parentLocation;
    
    return (
    <tr key={location._id} className="hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center space-x-3">
          <div className={`w-8 h-8 rounded flex items-center justify-center text-sm font-semibold ${getTypeColor(location.type)}`}>
            {getLocationIcon(location.type)}
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <p className="text-sm font-semibold text-gray-900">
                <DetailLink to={`/admin/locations/${location._id}`} className="hover:text-indigo-600">
                  {location.name}
                </DetailLink>
              </p>
            </div>
            <p className="text-xs text-gray-500 font-mono">{location.code}</p>
            {parentLocation && (
              <p className="text-xs text-indigo-600" title={`Parent: ${typeof parentLocation === 'object' ? parentLocation.name : 'Parent Location'}`}>
                ↳ {typeof parentLocation === 'object' ? parentLocation.name : 'Sublocation'}
              </p>
            )}
            {location.description && (
              <p className="text-xs text-gray-600 truncate" title={location.description}>
                {location.description}
              </p>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getTypeColor(location.type)}`}>
          {location.type?.toUpperCase()}
        </span>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm text-gray-700 line-clamp-2">
          {location.address?.street && `${location.address.street}, `}
          {location.address?.city}, {location.address?.state} {location.address?.zipCode || location.address?.postalCode}
        </p>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="text-sm text-gray-900">
          {contactInfo.email || 'N/A'}
        </div>
        <div className="text-sm text-gray-500">
          {contactInfo.phone || 'N/A'}
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
          isActive
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800'
        }`}>
          {isActive ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center justify-end space-x-2">
          <button
            onClick={() => navigateToDetail(`/admin/locations/${location._id}`)}
            className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded transition-all"
            title="View location"
          >
            <EyeIcon className="w-4 h-4" />
          </button>
          <PermissionGuard permission="ADMIN:LOCATIONS:UPDATE">
            <button
              onClick={() => {
                setEditingLocation(location);
                setShowLocationForm(true);
              }}
              className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all"
              title="Edit location"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
          </PermissionGuard>

          <PermissionGuard permission="ADMIN:LOCATIONS:DELETE">
            <button
              onClick={() => setShowDeleteConfirm(location)}
              className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-all"
              title="Delete location"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </PermissionGuard>
        </div>
      </td>
    </tr>
  )};


  const showLocationsSkeleton = loading && locations.length === 0;
  const showLocationsOverlay = loading && locations.length > 0;

  return (
    <div className="bg-white shadow-sm border border-gray-200">
      {/* Filters and Search */}
      <div className="px-4 sm:px-6 lg:px-8 py-2 border-b border-gray-200 flex-shrink-0">
        <div className="flex flex-col gap-2 min-w-0">
          {/* Search */}
          <div className="w-full min-w-0">
            <TextInput
              variant="input"
              type="text"
              placeholder="Search locations by name, code, or city..."
              value={searchInput}
              onChange={(e) => handleSearch(e.target.value)}
              icon={({ className }) => (
                <MagnifyingGlassIcon className={className} />
              )}
              className="text-sm py-1.5 border border-gray-300 shadow-sm !rounded-sm"
            />
          </div>

          {/* Filters and Actions */}
          <div className="flex flex-wrap items-center gap-2 w-full">
            {/* Status Filter */}
            <CustomSelect
              value={localActiveFilter === true ? 'active' : localActiveFilter === false ? 'inactive' : 'all'}
              onChange={(e) => {
                const value = e.target.value;
                handleActiveFilter(
                  value === 'all' ? undefined : value === 'active' ? true : false
                );
              }}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
              ]}
              placeholder=""
              size="sm"
              variant="default"
              className="min-w-[120px] !rounded-sm"
            />

            {/* Type Filter */}
            <CustomSelect
              value={localTypeFilter || ''}
              onChange={(e) => handleTypeFilter(e.target.value || null)}
              options={[
                { value: '', label: 'All Types' },
                { value: 'warehouse', label: 'Warehouse' },
                { value: 'branch', label: 'Branch' },
                { value: 'office', label: 'Office' },
                { value: 'factory', label: 'Factory' }
              ]}
              placeholder=""
              size="sm"
              variant="default"
              className="min-w-[120px] !rounded-sm"
            />

            {/* Reset Filters Button */}
            {hasModifiedFilters() && (
              <button
                onClick={handleResetFilters}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                title="Reset filters"
              >
                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset
              </button>
            )}

            {/* View Toggle */}
            <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5">
              <button
                onClick={() => handleViewChange('card')}
                className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'card'
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                title="Card view"
              >
                <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-7H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z" />
                </svg>
                Cards
              </button>
              <button
                onClick={() => handleViewChange('list')}
                className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'list'
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                title="List view"
              >
                <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                List
              </button>
            </div>

            {/* Add Location Button */}
            <PermissionGuard permission="ADMIN:LOCATIONS:CREATE">
              <button
                onClick={() => {
                  setEditingLocation(null);
                  setShowLocationForm(true);
                }}
                className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200"
              >
                <PlusIcon className="w-3.5 h-3.5 mr-1.5" />
                Add Location
              </button>
            </PermissionGuard>
          </div>
        </div>
      </div>

      {/* Pagination Section */}
      <div className="bg-gradient-to-r from-gray-100 to-gray-100 px-2 sm:px-4 lg:px-6 py-2 border-b border-gray-200 flex-shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {/* Results Info */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
              <MapPinIcon className="w-4 h-4 text-white" />
            </div>
            <div className="text-sm flex items-center gap-2">
              <div>
                <span className="font-semibold text-gray-900">
                  {((pagination.current - 1) * pagination.limit) + 1}-{Math.min(pagination.current * pagination.limit, pagination.total)}
                </span>
                <span className="text-gray-500 mx-1">of</span>
                <span className="font-semibold text-gray-900">{pagination.total}</span>
                <span className="text-gray-500 ml-1">locations</span>
              </div>
              {loading && locations.length > 0 && (
                <div className="flex items-center">
                  <svg className="animate-spin h-3 w-3 text-indigo-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              )}
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center gap-3">
            {/* Items per page */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-medium">Show:</span>
              <CustomSelect
                value={pagination.limit}
                onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                disabled={loading}
                options={[
                  { value: 10, label: '10' },
                  { value: 20, label: '20' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100' }
                ]}
                size="sm"
                variant="minimal"
                className="min-w-[70px]"
              />
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={pagination.current <= 1 || loading}
                className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                title="First page"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>

              <button
                onClick={() => handlePageChange(Math.max(1, pagination.current - 1))}
                disabled={pagination.current <= 1 || loading}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="hidden sm:inline">Previous</span>
              </button>

              {/* Page Numbers */}
              <div className="hidden md:flex items-center gap-1">
                {(() => {
                  const pages = [];
                  const currentPage = pagination.current;
                  const totalPages = pagination.pages;
                  const delta = 2; // Number of pages to show on each side of current page

                  // Always show first page
                  pages.push(
                    <button
                      key={1}
                      onClick={() => handlePageChange(1)}
                      disabled={loading}
                      className={`min-w-[32px] px-2.5 py-1.5 rounded text-sm font-medium transition-all ${
                        currentPage === 1
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                      }`}
                    >
                      1
                    </button>
                  );

                  // Show ellipsis if there's a gap after first page
                  if (currentPage > delta + 2) {
                    pages.push(
                      <span key="ellipsis-start" className="px-2 text-gray-400">
                        ...
                      </span>
                    );
                  }

                  // Show pages around current page
                  const start = Math.max(2, currentPage - delta);
                  const end = Math.min(totalPages - 1, currentPage + delta);

                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        disabled={loading}
                        className={`min-w-[32px] px-2.5 py-1.5 rounded text-sm font-medium transition-all ${
                          currentPage === i
                            ? 'bg-indigo-600 text-white shadow-sm'
                            : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  // Show ellipsis if there's a gap before last page
                  if (currentPage < totalPages - delta - 1) {
                    pages.push(
                      <span key="ellipsis-end" className="px-2 text-gray-400">
                        ...
                      </span>
                    );
                  }

                  // Always show last page if there's more than 1 page
                  if (totalPages > 1) {
                    pages.push(
                      <button
                        key={totalPages}
                        onClick={() => handlePageChange(totalPages)}
                        disabled={loading}
                        className={`min-w-[32px] px-2.5 py-1.5 rounded text-sm font-medium transition-all ${
                          currentPage === totalPages
                            ? 'bg-indigo-600 text-white shadow-sm'
                            : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                        }`}
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </div>

              <button
                onClick={() => handlePageChange(Math.min(pagination.pages, pagination.current + 1))}
                disabled={pagination.current >= pagination.pages || loading}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
              >
                <span className="hidden sm:inline">Next</span>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button
                onClick={() => handlePageChange(pagination.pages)}
                disabled={pagination.current >= pagination.pages || loading}
                className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                title="Last page"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Pagination */}
        <div className="flex md:hidden items-center justify-center gap-1 mt-3 pt-3 border-t border-gray-200">
          {Array.from({ length: Math.min(7, pagination.pages) }, (_, i) => {
            const pageNum = i + 1;
            const isCurrentPage = pageNum === pagination.current;
            
            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                disabled={loading}
                className={`min-w-[32px] px-2.5 py-1.5 rounded text-sm font-medium transition-all ${
                  isCurrentPage
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                }`}
              >
                {pageNum}
              </button>
            );
          })}
          {pagination.pages > 7 && (
            <span className="px-2 text-gray-400 text-sm">...</span>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="p-2">
        {showLocationsSkeleton ? (
          view === 'list' ? (
            <TableSkeleton
              columns={6}
              rows={Math.min(pagination.limit || 20, 10)}
              columnWidths={['w-28', 'w-16', 'w-36', 'w-24', 'w-14', 'w-16']}
            />
          ) : (
            <CardGridSkeleton count={8} />
          )
        ) : (
          <DataViewLoadingContainer
            loading={showLocationsOverlay}
            message={view === 'list' ? 'Loading locations…' : 'Loading…'}
          >
            {view === 'card' ? (
              <div className="px-2 py-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                  {locations.length === 0 ? (
                    <div className="col-span-full bg-white border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                      <MapPinIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <h3 className="text-sm font-medium text-gray-900 mb-2">No locations found</h3>
                      <p className="text-sm text-gray-500 mb-4">
                        {searchInput || localTypeFilter || localActiveFilter !== true
                          ? 'Try adjusting your search or filters'
                          : 'Get started by creating your first location'}
                      </p>
                      {!searchInput && !localTypeFilter && localActiveFilter === true && (
                        <PermissionGuard permission="ADMIN:LOCATIONS:CREATE">
                          <button
                            onClick={() => {
                              setEditingLocation(null);
                              setShowLocationForm(true);
                            }}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                          >
                            <PlusIcon className="w-4 h-4 mr-2" />
                            Add Location
                          </button>
                        </PermissionGuard>
                      )}
                    </div>
                  ) : (
                    locations.map((location) => renderLocationCard(location))
                  )}
                </div>
              </div>
            ) : (
              <div className="overflow-hidden">
                <div className="overflow-x-auto max-h-[70vh]">
                  <table className="min-w-max w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Location
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Address
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {locations.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-4 py-12 text-center">
                            <MapPinIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                            <h3 className="text-sm font-medium text-gray-900 mb-2">No locations found</h3>
                            <p className="text-sm text-gray-500">
                              {searchInput || localTypeFilter || localActiveFilter !== true
                                ? 'Try adjusting your search or filters'
                                : 'Get started by creating your first location'}
                            </p>
                          </td>
                        </tr>
                      ) : (
                        locations.map((location) => renderLocationRow(location))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </DataViewLoadingContainer>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-md bg-white rounded-lg shadow-2xl border border-gray-100">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <TrashIcon className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Delete Location
              </h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                Are you sure you want to delete <span className="font-semibold">{showDeleteConfirm.name}</span>?
                This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm._id, showDeleteConfirm.name)}
                  className="flex-1 px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Location Form Modal */}
      {showLocationForm && (
        <LocationForm
          location={editingLocation}
          onSuccess={() => {
            setShowLocationForm(false);
            setEditingLocation(null);
            fetchLocations();
          }}
          onCancel={() => {
            setShowLocationForm(false);
            setEditingLocation(null);
          }}
        />
      )}
    </div>
  );
};

export default LocationsList;
